import { useEffect, useState } from 'react';

import { fetchMyLaunchAccess } from 'components';

/**
 * Whether this trainer sees "Launch from Scenario" on their dashboard.
 *
 * THE ANSWER COMES FROM THE SERVER. `GET /scenario-builder/launch-access/mine`
 * returns the one decision an administrator manages on the Trainers screen: an
 * explicit grant, an explicit withdrawal, or — with no row on file — the
 * derived default, which is on for a trainer who runs a class with trainees in
 * it. Administrators are never gated.
 *
 * WHAT THIS REPLACES. The switch used to be `?scenarioLaunch=1`, a URL
 * parameter persisted in this browser's local storage. It could not be granted
 * by an admin, could not be audited, could not be withdrawn, and — the reason
 * it had to go — a trainer who never typed it never saw the feature at all. The
 * product QA measured the consequence: zero launch buttons in the field, with
 * the whole scenario pipeline sitting behind one.
 *
 * THE FALLBACK, AND WHY IT IS SAFE. Until the route is deployed the server
 * cannot answer, and this hook then falls back to the old parameter so the
 * running pilot keeps working; the moment the route answers, the server's word
 * is final in both directions. Neither path is a security boundary — the
 * blueprint launch endpoint enforces its own permissions server-side, and this
 * hook only decides whether a button is drawn.
 */
const STORAGE_KEY = 'vega.dev.scenarioLaunch';
const PARAM = 'scenarioLaunch';

const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * The legacy pilot flag: a `?scenarioLaunch=1|0` URL param wins and is
 * persisted, otherwise the stored flag stands. Consulted only while the server
 * has not answered.
 */
const resolveLegacyFlag = (): boolean => {
  try {
    const value = new URLSearchParams(window.location.search).get(PARAM);
    if (value === '0' || value === 'false') {
      window.localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    if (value !== null) {
      window.localStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
  } catch {
    /* ignore quota / privacy-mode failures — in-memory state still resolves */
  }
  return readStored();
};

export function useScenarioLaunchAccess(): boolean {
  // Starts from the legacy flag so a pilot trainer's button does not flicker
  // off and back on while the request is in flight.
  const [enabled, setEnabled] = useState<boolean>(resolveLegacyFlag);

  useEffect(() => {
    let active = true;
    fetchMyLaunchAccess().then(answer => {
      // `undefined` means the server could not answer at all. Anything else is
      // the decision, and it overrides the legacy flag in both directions.
      if (active && answer) setEnabled(answer.allowed);
    });
    return () => {
      active = false;
    };
  }, []);

  return enabled;
}
